import FilterDropdown from '@components/ui/Dropdown';
import { DataSheet, DataSheetPagination, dataSheetTokens, type DataSheetColumn, type DataSheetFilterConfig } from '@components/ui/DataSheet';
import { endOfISOWeek, endOfMonth, format, startOfISOWeek, startOfMonth, subDays, subMonths } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { formatDisplayRange } from './dateUtils';
import { formatCompactNumber } from './numberUtils';
import type { DateRange, PeriodMeta, PeriodType, SkuPivotRow } from './types';

const tokens = dataSheetTokens;

type DemandTableSectionProps = {
    periods: PeriodMeta[];
    rows: SkuPivotRow[];
    preferredPeriodType: PeriodType;

    tableRange: DateRange;
    tableSearch: string;
    channelFilterConfig: DataSheetFilterConfig;
    productFilterConfig: DataSheetFilterConfig;

    showCalendar: boolean;
    onOpenCalendar: () => void;
    onCloseCalendar: () => void;
    onApplyRange: (r: DateRange) => void;

    onChangeSearch: (v: string) => void;

    onDownloadCsv: () => void;
    onUploadForecast?: () => void;
    onShare?: () => void;
    onAddToChat?: () => void;

    totalSales: number;
    totalForecast: number;
    forecastAccuracyLabel: string;
};

const DemandTableSection: React.FC<DemandTableSectionProps> = ({
    periods,
    rows,
    preferredPeriodType,
    tableRange,
    tableSearch,
    channelFilterConfig,
    productFilterConfig,
    showCalendar,
    onOpenCalendar,
    onCloseCalendar,
    onApplyRange,
    onChangeSearch,
    onDownloadCsv,
    onUploadForecast,
    onShare,
    onAddToChat,
    totalSales,
    totalForecast,
    forecastAccuracyLabel,
}) => {
    const hasData = periods.length > 0 && rows.length > 0;
    const isMonthly = preferredPeriodType === 'MONTH';
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Build DataSheet columns dynamically
    const columns = useMemo<DataSheetColumn<SkuPivotRow>[]>(() => {
        const cols: DataSheetColumn<SkuPivotRow>[] = [];

        // SKU column (sticky)
        cols.push({
            key: 'sku',
            header: 'SKU',
            width: 200,
            sticky: true,
            render: (value, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'rgb(15, 23, 42)' }}>
                        {row.name || '—'}
                    </span>
                    <span style={{ fontSize: 11, color: 'rgb(100, 116, 139)' }}>
                        {row.sku}
                    </span>
                </div>
            ),
        });

        // For each period -> Forecast + Actual columns
        periods.forEach((p) => {
            cols.push({
                key: `period_${p.key}_forecast`,
                header: 'Forecast',
                group: p.label,
                align: 'right',
                width: 90,
                render: (_: any, row: SkuPivotRow) => {
                    const metric = row.metricsByPeriod[p.key] || {};
                    const f = metric.forecast;
                    
                    return (
                        <div style={{
                            backgroundColor: 'rgba(239, 246, 255, 0.6)',
                            borderRadius: 4,
                            padding: '4px 8px',
                            fontSize: 11,
                            fontVariantNumeric: 'tabular-nums',
                            textAlign: 'right',
                        }}>
                            {formatCompactNumber(typeof f === 'number' ? f : null)}
                        </div>
                    );
                },
            });

            cols.push({
                key: `period_${p.key}_actual`,
                header: 'Actual',
                group: p.label,
                align: 'right',
                width: 90,
                render: (_: any, row: SkuPivotRow) => {
                    const metric = row.metricsByPeriod[p.key] || {};
                    const f = metric.forecast;
                    const a = metric.actual;

                    // Green when actual beats forecast, red when it under-runs.
                    // Neutral if either side is missing or values match.
                    const hasBoth = typeof f === 'number' && typeof a === 'number';
                    const isOver = hasBoth && (a as number) > (f as number);
                    const isUnder = hasBoth && (a as number) < (f as number);

                    const backgroundColor = isOver
                        ? 'rgba(240, 253, 244, 0.6)'
                        : isUnder
                        ? 'rgba(254, 226, 226, 0.6)'
                        : 'transparent';
                    const color = isOver
                        ? 'rgb(21, 128, 61)'
                        : isUnder
                        ? 'rgb(185, 28, 28)'
                        : 'rgb(15, 23, 42)';

                    return (
                        <div style={{
                            backgroundColor,
                            color,
                            borderRadius: 4,
                            padding: '4px 8px',
                            fontSize: 11,
                            fontVariantNumeric: 'tabular-nums',
                            textAlign: 'right',
                        }}>
                            {formatCompactNumber(typeof a === 'number' ? a : null)}
                        </div>
                    );
                },
            });

            cols.push({
                key: `period_${p.key}_variance`,
                header: 'Variance',
                group: p.label,
                align: 'right',
                width: 90,
                render: (_: any, row: SkuPivotRow) => {
                    const metric = row.metricsByPeriod[p.key] || {};
                    const v = metric.variance;

                    if (v == null) {
                        return (
                            <div style={{
                                borderRadius: 4,
                                padding: '4px 8px',
                                fontSize: 11,
                                fontVariantNumeric: 'tabular-nums',
                                textAlign: 'right',
                                color: 'rgb(148, 163, 184)',
                            }}>
                                —
                            </div>
                        );
                    }

                    const isHigh = v > 20;
                    const isGood = v <= 0;
                    const backgroundColor = isHigh
                        ? 'rgba(254, 226, 226, 0.6)'
                        : isGood
                        ? 'rgba(240, 253, 244, 0.6)'
                        : 'rgba(254, 249, 195, 0.6)';
                    const color = isHigh
                        ? 'rgb(185, 28, 28)'
                        : isGood
                        ? 'rgb(21, 128, 61)'
                        : 'rgb(133, 77, 14)';
                    const sign = v > 0 ? '+' : '';

                    return (
                        <div style={{
                            backgroundColor,
                            color,
                            borderRadius: 4,
                            padding: '4px 8px',
                            fontSize: 11,
                            fontVariantNumeric: 'tabular-nums',
                            textAlign: 'right',
                        }}>
                            {`${sign}${v.toFixed(1)}%`}
                        </div>
                    );
                },
            });
        });

        return cols;
    }, [periods]);

    // Derived range for "Showing data for..." to match the actual columns
    const displayRange = useMemo<DateRange>(() => {
        if (periods.length > 0) {
            return {
                startDate: periods[0].startDate,
                endDate: periods[periods.length - 1].endDate,
            };
        }
        return tableRange;
    }, [periods, tableRange]);

    // Paginated data
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return rows.slice(start, start + pageSize);
    }, [rows, currentPage, pageSize]);

    const totalPages = Math.ceil(rows.length / pageSize);

    const aggregateRow = useMemo(() => {
        const result: Record<string, string> = {};
        periods.forEach((p) => {
            const forecastSum = rows.reduce((sum, row) => {
                const f = row.metricsByPeriod[p.key]?.forecast;
                return sum + (typeof f === 'number' ? f : 0);
            }, 0);
            const actualSum = rows.reduce((sum, row) => {
                const a = row.metricsByPeriod[p.key]?.actual;
                return sum + (typeof a === 'number' ? a : 0);
            }, 0);
            result[`period_${p.key}_forecast`] = formatCompactNumber(forecastSum) ?? '';
            result[`period_${p.key}_actual`] = formatCompactNumber(actualSum) ?? '';
        });
        return result;
    }, [rows, periods]);

    return (
        <section style={{ marginTop: tokens.spacing.lg }}>
            {/* Section Header */}
            <div style={{ marginBottom: tokens.spacing.md, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: tokens.spacing.sm }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: tokens.spacing.sm, flexWrap: 'wrap' }}>
                    <h2 style={{ fontSize: 18, fontWeight: 600, color: tokens.colors.textPrimary }}>
                        Forecast vs Actual
                    </h2>
                    <span style={{ fontSize: 12, color: tokens.colors.textMuted }}>
                        Forecast Accuracy:{' '}
                        <span style={{ fontWeight: 600, color: tokens.colors.textPrimary }}>
                            {forecastAccuracyLabel}
                        </span>{' '}
                        · Total Sales:{' '}
                        <span style={{ fontWeight: 600, color: tokens.colors.textPrimary }}>
                            {formatCompactNumber(totalSales)} units
                        </span>{' '}
                        · Forecasted:{' '}
                        <span style={{ fontWeight: 600, color: tokens.colors.textPrimary }}>
                            {formatCompactNumber(totalForecast)} units
                        </span>
                    </span>
                </div>
                {onUploadForecast && (
                    <button
                        type="button"
                        onClick={onUploadForecast}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            height: 32,
                            padding: '0 12px',
                            fontSize: 12,
                            fontWeight: 500,
                            color: 'rgb(15, 23, 42)',
                            backgroundColor: 'white',
                            border: '1px solid rgb(226, 232, 240)',
                            borderRadius: 6,
                            cursor: 'pointer',
                        }}
                    >
                        Upload Forecast
                    </button>
                )}
            </div>

            {/* DataSheet with integrated toolbar */}
            <DataSheet<SkuPivotRow>
                data={paginatedRows}
                columns={columns}
                aggregateRow={aggregateRow}
                title="SKU Sales Data"
                subtitle={`Showing ${formatDisplayRange(displayRange)} · Cells highlighted in red show variance >20%`}
                onAddToChat={onAddToChat}
                enableSearch
                searchPlaceholder="Search SKU..."
                searchValue={tableSearch}
                onSearchChange={onChangeSearch}
                enableDownload
                onDownload={onDownloadCsv}
                onShare={onShare}
                filters={[channelFilterConfig, productFilterConfig]}
                toolbarControls={
                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing.xs }}>
                        <FilterDropdown
                            value={
                                isMonthly
                                    ? // Monthly options matching (current month always included)
                                      [
                                          { months: 0, label: 'Current Month' },
                                          { months: 1, label: 'Last 1 Month' },
                                          { months: 2, label: 'Last 2 Months' },
                                          { months: 4, label: 'Last 4 Months' },
                                          { months: 6, label: 'Last 6 Months' },
                                      ].find((opt) => {
                                          const today = new Date();
                                          // End is always end of current month
                                          const e = endOfMonth(today);
                                          // Start goes back X months from current month
                                          const startMonth = subMonths(today, opt.months);
                                          const s = startOfMonth(startMonth);
                                          return (
                                              tableRange.startDate === format(s, 'yyyy-MM-dd') &&
                                              tableRange.endDate === format(e, 'yyyy-MM-dd')
                                          );
                                      })?.label || 'Custom'
                                    : // Weekly options matching (current week always included)
                                      [
                                          { weeks: 0, label: 'Current Week' },
                                          { weeks: 1, label: 'Last 1 Week' },
                                          { weeks: 2, label: 'Last 2 Weeks' },
                                          { weeks: 4, label: 'Last 4 Weeks' },
                                          { weeks: 8, label: 'Last 8 Weeks' },
                                          { weeks: 12, label: 'Last 12 Weeks' },
                                      ].find((opt) => {
                                          const today = new Date();
                                          // End is always end of current week
                                          const e = endOfISOWeek(today);
                                          // Start goes back X weeks from current week
                                          const start = subDays(startOfISOWeek(today), opt.weeks * 7);
                                          return (
                                              tableRange.startDate === format(start, 'yyyy-MM-dd') &&
                                              tableRange.endDate === format(e, 'yyyy-MM-dd')
                                          );
                                      })?.label || 'Custom'
                            }
                            onChange={(val) => {
                                const today = new Date();

                                if (isMonthly) {
                                    // Handle monthly options - current month always included
                                    if (val === 'Current Month') {
                                        onApplyRange({
                                            startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
                                            endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
                                        });
                                        return;
                                    }

                                    const monthsNum = parseInt(
                                        val.replace('Last ', '').replace(' Month', '').replace('s', ''),
                                    );
                                    if (!isNaN(monthsNum)) {
                                        // End is always end of current month
                                        const endDate = endOfMonth(today);
                                        // Start goes back X months from current month
                                        const startMonth = subMonths(today, monthsNum);
                                        onApplyRange({
                                            startDate: format(startOfMonth(startMonth), 'yyyy-MM-dd'),
                                            endDate: format(endDate, 'yyyy-MM-dd'),
                                        });
                                    }
                                } else {
                                    // Handle weekly options - current week always included
                                    if (val === 'Current Week') {
                                        onApplyRange({
                                            startDate: format(startOfISOWeek(today), 'yyyy-MM-dd'),
                                            endDate: format(endOfISOWeek(today), 'yyyy-MM-dd'),
                                        });
                                        return;
                                    }

                                    const weeksNum = parseInt(
                                        val.replace('Last ', '').replace(' Week', '').replace('s', ''),
                                    );
                                    if (!isNaN(weeksNum)) {
                                        // End is always end of current week
                                        const endDate = endOfISOWeek(today);
                                        // Start goes back X weeks from current week start
                                        const start = subDays(startOfISOWeek(today), weeksNum * 7);
                                        onApplyRange({
                                            startDate: format(start, 'yyyy-MM-dd'),
                                            endDate: format(endDate, 'yyyy-MM-dd'),
                                        });
                                    }
                                }
                            }}
                            options={
                                isMonthly
                                    ? [
                                          { value: 'Current Month', label: 'Current Month' },
                                          { value: 'Last 1 Month', label: 'Last 1 Month' },
                                          { value: 'Last 2 Months', label: 'Last 2 Months' },
                                          { value: 'Last 4 Months', label: 'Last 4 Months' },
                                          { value: 'Last 6 Months', label: 'Last 6 Months' },
                                      ]
                                    : [
                                          { value: 'Current Week', label: 'Current Week' },
                                          { value: 'Last 1 Week', label: 'Last 1 Week' },
                                          { value: 'Last 2 Weeks', label: 'Last 2 Weeks' },
                                          { value: 'Last 4 Weeks', label: 'Last 4 Weeks' },
                                          { value: 'Last 8 Weeks', label: 'Last 8 Weeks' },
                                          { value: 'Last 12 Weeks', label: 'Last 12 Weeks' },
                                      ]
                            }
                            placeholder="Period"
                        />
                    </div>
                }
                maxHeight="auto"
                getRowKey={(row) => row.sku}
                emptyState="No data available"
                footer={
                    <DataSheetPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        totalItems={rows.length}
                        pageSizeOptions={[20, 50, 100]}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        }}
                    />
                }
            />
        </section>
    );
};

export default DemandTableSection;
